/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import { Modal, Tabs, Descriptions, Tag } from 'antd';
import { ModalProps } from 'antd/lib/modal';
import { MovementEntry } from '~components/atoms/FileTables/TenderMovements/TenderMovementsTable';
import { formatDate } from '~src/utils/formatDate';
import { getReadableType } from '~src/utils/movementsUtils/movementsUtils.tsx';
import { useIntl } from 'react-intl';

interface MovementDetailsModalProps extends ModalProps {
  movement?: MovementEntry;
}

export const MovementDetailsModal: React.FC<MovementDetailsModalProps> = ({
  movement,
  onCancel,
  ...modalProps
}) => {
  const intl = useIntl();
  if (!movement) return null;

  return (
    <Modal
      title={`${intl.formatMessage({
        id: 'movement-details',
      })} - ${getReadableType(movement.type)}`}
      width="min(90vw, 800px)"
      style={{ top: 20 }}
      bodyStyle={{
        padding: '0',
        maxHeight: '85vh',
        overflowY: 'auto',
      }}
      centered={false}
      destroyOnClose
      maskClosable={true}
      onCancel={onCancel}
      footer={null}
      {...modalProps}>
      <div style={{ padding: '20px' }}>
        <Descriptions
          bordered
          column={2}
          size="small"
          style={{ marginBottom: '20px' }}>
          <Descriptions.Item
            label={`${intl.formatMessage({ id: 'id' })}`}
            span={2}>
            {movement.originalId || movement.id}
          </Descriptions.Item>
          <Descriptions.Item
            label={`${intl.formatMessage({ id: 'type' })}`}
            span={2}>
            {getReadableType(movement.type)}
          </Descriptions.Item>
          <Descriptions.Item label={`${intl.formatMessage({ id: 'actor' })}`}>
            {movement.created_by_user_name}
          </Descriptions.Item>
          <Descriptions.Item label={`${intl.formatMessage({ id: 'time' })}`}>
            {formatDate(movement.created_at, 'long')}
          </Descriptions.Item>

          {movement.summary && (
            <Descriptions.Item
              label={`${intl.formatMessage({ id: 'summary' })}`}
              span={2}>
              <div>
                <h3 style={{ fontSize: '16px', marginBottom: 8 }}>
                  {movement.summary.head}
                </h3>
                {movement.summary.actions?.map((action, idx) => (
                  <div
                    key={idx}
                    style={{
                      fontSize: '14px',
                      color: '#666',
                      marginLeft: 16,
                    }}>
                    • {action}
                  </div>
                ))}
              </div>
            </Descriptions.Item>
          )}
        </Descriptions>

        {/* Custom Note Section */}
        {movement.custom && movement.custom.created_at > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <h3
              style={{
                fontSize: '16px',
                marginBottom: '10px',
                borderBottom: '1px solid #eee',
                paddingBottom: '5px',
              }}>
              {intl.formatMessage({ id: 'custom-note' })}
            </h3>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item
                label={`${intl.formatMessage({ id: 'author' })}`}>
                {movement.custom.created_by_user_name}
              </Descriptions.Item>
              <Descriptions.Item
                label={`${intl.formatMessage({ id: 'time' })}`}>
                {formatDate(movement.custom.created_at, 'long')}
              </Descriptions.Item>
              <Descriptions.Item
                label={`${intl.formatMessage({ id: 'note' })}`}>
                {movement.custom.note || '—'}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}

        {/* Changes Section */}
        {movement.changes && movement.changes.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <h3
              style={{
                fontSize: '16px',
                marginBottom: '10px',
                borderBottom: '1px solid #eee',
                paddingBottom: '5px',
              }}>
              {intl.formatMessage({ id: 'movement-changes' })}
            </h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th
                    style={{
                      padding: '8px',
                      border: '1px solid #ddd',
                      width: '30%',
                    }}>
                    {intl.formatMessage({ id: 'field' })}
                  </th>
                  <th
                    style={{
                      padding: '8px',
                      border: '1px solid #ddd',
                      width: '35%',
                    }}>
                    {intl.formatMessage({ id: 'old-value' })}
                  </th>
                  <th
                    style={{
                      padding: '8px',
                      border: '1px solid #ddd',
                      width: '35%',
                    }}>
                    {intl.formatMessage({ id: 'new-value' })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {movement.changes.map((change, idx) => (
                  <tr key={idx}>
                    <td
                      style={{
                        padding: '8px',
                        border: '1px solid #ddd',
                        fontWeight: 'bold',
                      }}>
                      {change.field}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                      {change.old_value || '—'}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                      {change.new_value || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Target Entities Section */}
        {movement.target_entities && movement.target_entities.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <h3
              style={{
                fontSize: '16px',
                marginBottom: '10px',
                borderBottom: '1px solid #eee',
                paddingBottom: '5px',
              }}>
              {intl.formatMessage({ id: 'target-entities' })}
            </h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th
                    style={{
                      padding: '8px',
                      border: '1px solid #ddd',
                      width: '20%',
                    }}>
                    {intl.formatMessage({ id: 'type' })}
                  </th>
                  <th
                    style={{
                      padding: '8px',
                      border: '1px solid #ddd',
                      width: '40%',
                    }}>
                    {intl.formatMessage({ id: 'name' })}
                  </th>
                  <th
                    style={{
                      padding: '8px',
                      border: '1px solid #ddd',
                      width: '40%',
                    }}>
                    {intl.formatMessage({ id: 'description' })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {movement.target_entities.map((entity, idx) => (
                  <tr key={idx}>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                      {entity.type}
                    </td>
                    <td
                      style={{
                        padding: '8px',
                        border: '1px solid #ddd',
                        fontWeight: 'bold',
                      }}>
                      {entity.display_name}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                      {entity.description || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
};
